import React, { useMemo } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Plus } from 'lucide-react';
import calendarIcon from '@/assets/icons/calendar.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EmptyStateCard } from '@/components/ui/EmptyStateCard';
import { useInstructorClasses, InstructorClass } from '@/hooks/useInstructorClasses';
import { useSmartDateSelection } from '@/hooks/useSmartDateSelection';
import { InstructorSlotsSection } from './InstructorSlotsSection';
import { isSameDay, format, addDays } from 'date-fns';
import { useAvailabilitySlots } from '@/hooks/useAvailabilitySlots';
import { useAgendaConfig } from '@/hooks/useAgendaConfig';
import { useAuth } from '@/contexts/AuthContext';

const NextClassesSection: React.FC = () => {
  const { t, currentLanguage } = useI18n();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config: agendaConfig } = useAgendaConfig();
  
  // Use smart date selection (today or next available working day)
  const selectedDate = useSmartDateSelection();
  const { classes, isLoading, error, refetch } = useInstructorClasses(selectedDate);

  // Buscar configuração da agenda
  const effectiveConfig = (agendaConfig && typeof agendaConfig === 'object' && !Array.isArray(agendaConfig)) ? agendaConfig : {
    instructor_id: user?.id || '',
    working_days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    working_hours: {
      mon: { start: '08:00', end: '18:00' },
      tue: { start: '08:00', end: '18:00' },
      wed: { start: '08:00', end: '18:00' },
      thu: { start: '08:00', end: '18:00' },
      fri: { start: '08:00', end: '18:00' }
    },
    default_duration: 60,
    is_public: false
  };

  // Calcular próximos 3 dias úteis
  const nextThreeWorkingDays = useMemo(() => {
    const workingDays = effectiveConfig.working_days || ['mon', 'tue', 'wed', 'thu', 'fri'];
    const days = [];
    let currentDay = selectedDate;
    let attempts = 0;
    const maxAttempts = 10; // Evitar loop infinito
    
    while (days.length < 3 && attempts < maxAttempts) {
      const dayName = currentDay.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
      
      if (workingDays.includes(dayName)) {
        days.push(currentDay);
      }
      
      currentDay = addDays(currentDay, 1);
      attempts++;
    }
    
    return days;
  }, [selectedDate, effectiveConfig]);

  // Buscar slots para os próximos 3 dias úteis
  const slotsData = useMemo(() => {
    if (!nextThreeWorkingDays || nextThreeWorkingDays.length === 0) {
      return [];
    }
    
    return nextThreeWorkingDays.map(date => {
      // Note: useAvailabilitySlots não pode ser chamado dentro de useMemo
      // Vamos retornar dados vazios por enquanto e implementar de outra forma
      return {
        date,
        slots: []
      };
    });
  }, [nextThreeWorkingDays]);

  // Forçar recarregamento quando o usuário retorna para a página Home
  useEffect(() => {
    if (location.pathname === '/home') {
      // Invalidar queries relacionadas para forçar recarregamento
      queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
      queryClient.invalidateQueries({ queryKey: ['instructor-highlights'] });
      
      // Forçar refetch imediato
      refetch();
    }
  }, [location.pathname, queryClient, refetch]);

  const handleSeeAll = () => {
    navigate('/calendar');
  };

  const handleAddClass = () => {
    navigate('/calendar');
    // TODO: Open Add Class modal
  };

  // Função para traduzir o tipo de aula
  const getClassTypeTranslation = (type: string) => {
    const translations: Record<string, string> = {
      'private': 'Private Class',
      'group': 'Group Class',
      'seminar': 'Seminar',
      'competition': 'Competition',
      'open_mat': 'Open Mat',
    };
    return translations[type] || type;
  };

  // Função para formatar a data
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = dayNames[date.getDay()];
    return `${day} ${dayName}`;
  };

  // Função para formatar o horário
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(currentLanguage === 'pt' ? 'pt-BR' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  if (isLoading) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          Next Classes
        </h2>
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-8">
        <h2 className="text-sm font-bold text-white font-roboto mb-4">
          Next Classes
        </h2>
        <div className="text-red-400">Error loading classes</div>
      </div>
    );
  }

  // Always show the section, even if there are no classes
  // The slots section may have available slots even without scheduled classes

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold text-white font-roboto">
          Next Classes {selectedDate && !isSameDay(selectedDate, new Date()) && (
            <span className="text-xs font-normal text-gray-400 ml-2">
              ({format(selectedDate, 'MMM dd')})
            </span>
          )}
        </h2>
        <button
          onClick={handleSeeAll}
          className="text-xs text-white underline hover:text-gray-300 transition-colors"
        >
          See All
        </button>
      </div>

      <div className="relative max-w-full mx-auto">
        <div className="w-full overflow-x-auto pb-2 scrollbar-hide">
          <div className="flex space-x-4">
            {/* Classes agendadas - only show if there are classes */}
            {classes && classes.length > 0 && classes.slice(0, 2).map((classItem) => {
              const dateInfo = formatDate(classItem.start_time);
              const startTime = formatTime(classItem.start_time);
              const endTime = formatTime(classItem.end_time);
              
              return (
                <div key={classItem.id} className="flex-shrink-0 w-[225px] bg-[rgb(45,36,36)] rounded-lg p-4 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <div className="mb-3">
                    <div className="text-sm font-bold text-white mb-2 font-roboto">
                      {getClassTypeTranslation(classItem.type)} - {classItem.modality}
                    </div>
                    <div className="text-xs text-gray-300 font-roboto flex items-center space-x-2">
                      <img src={calendarIcon} alt="Calendar" className="w-4 h-4" />
                      <span>{dateInfo} - {startTime} to {endTime}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Seção de slots disponíveis - always show */}
            <InstructorSlotsSection classes={classes} selectedDate={selectedDate} />
            
            {/* Mostrar slots dos próximos 3 dias úteis quando não há aulas agendadas */}
            {(!classes || classes.length === 0) && slotsData && slotsData.map((slotData, index) => {
              const totalSlots = (slotData.slots || []).reduce((total: number, slot: any) => total + (slot?.slotCount || 0), 0);
              const firstSlot = slotData.slots?.[0];
              const lastSlot = slotData.slots?.[slotData.slots?.length - 1];
              
              const formatTime = (timeString: string) => {
                const [hours, minutes] = timeString.split(':').map(Number);
                const date = new Date();
                date.setHours(hours, minutes);
                return date.toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                });
              };
              
              return (
                <div key={`slots-${index}`} className="flex-shrink-0 w-[219px] h-[124px] relative">
                  {/* Card com borda tracejada grossa */}
                  <div className="absolute inset-0 bg-transparent border-2 border-white border-dashed rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" />
                  
                  {/* Conteúdo */}
                  <div className="absolute inset-0 flex flex-col justify-between p-[17px]">
                    {/* Título e horário */}
                    <div>
                      <h3 className="font-['Roboto'] font-bold text-[13px] text-white leading-normal tracking-[0.1px]">
                        {totalSlots > 0 ? `${totalSlots} slots available` : 'Slots available'}
                        <span className="block text-[11px] font-normal text-gray-300 mt-1">
                          {format(slotData.date, 'MMM dd, yyyy')}
                        </span>
                      </h3>
                      
                      <p className="font-['Roboto'] font-normal text-[12px] text-white leading-[17.5px]">
                        {totalSlots > 0 && firstSlot && lastSlot ? (
                          <>
                            {formatTime(firstSlot.startTime)} - {formatTime(lastSlot.endTime)}
                          </>
                        ) : (
                          'No slots available'
                        )}
                      </p>
                    </div>
                    
                    {/* Botão Add Class */}
                    <div className="flex justify-center">
                      <button 
                        onClick={handleAddClass}
                        className="flex items-center justify-center gap-2 h-6 px-[30px] py-2 border border-white rounded-full bg-transparent hover:bg-white/10 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="font-['Roboto'] font-bold text-[12px] text-white leading-normal">
                          Add Class
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>


    </div>
  );
};

export default NextClassesSection;
